import {Trans, useTranslation} from '@/translations/i18n'
import {AccessDashboardCard} from '../access-dashboard-card/AccessDashboardCard'
import {useGetAccessesDashboard} from '../../queries/useGetAccessesDashboard'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {filtersParamToPayload} from '@/utilities/helpers'
import {AccessesTableDashboardEmptyState} from '../accesses-table-dashboard/AcccessesTableDashboardEmptyState'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {ArrowDownIcon, ArrowUpIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {StyledDot} from '../accesses-table-dashboard/style'
import {AccessesSearchParams} from '../../types'

export const AccessesDashboardTreatmentsCard = () => {
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(AccessesSearchParams)

    const {data, isLoading} = useGetAccessesDashboard<'treatments'>({
        payload: {
            filters: filtersParamToPayload(searchParams.filters)?.filters
        },
        params: {
            card: 'treatments',
            startDate: searchParams.startDate,
            endDate: searchParams.endDate
        }
    })

    const {palette} = useTheme()

    if (isLoading || !data) {
        return <AccessesTableDashboardEmptyState title={t('accesses:dashboard_treatments')} isLoading={isLoading} />
    }

    return (
        <AccessDashboardCard title={t('accesses:dashboard_treatments')}>
            <Flexbox gap={2} align="center" style={{flexWrap: 'wrap'}}>
                <Text size="textSm" palette="neutral" shade="600">
                    <Trans
                        i18nKey="accesses:dashboard_treatments_subtitle"
                        values={{count: data?.treatments.planned, total: data?.treatments.done}}
                        components={[
                            <Text render="span" size="textLg" palette="neutral" shade="950" weight="600" />,
                            <Text render="span" size="textLg" palette="neutral" shade="600" weight="500" />
                        ]}
                    />
                </Text>

                {data?.treatments.difference && (
                    <>
                        {data?.treatments.difference > 0 ? (
                            <Flexbox align="center">
                                <ArrowDownIcon size={15} color={palette.red[500]} />
                                <Text size="textSm" palette="danger" shade="500">
                                    <Trans
                                        i18nKey="accesses:less_accesses"
                                        values={{accesses: data?.treatments.difference}}
                                        components={[
                                            <Text
                                                render="span"
                                                size="textMd"
                                                palette="danger"
                                                shade="500"
                                                weight="600"
                                            />
                                        ]}
                                    />
                                </Text>
                            </Flexbox>
                        ) : data?.treatments.difference < 0 ? (
                            <Flexbox align="center">
                                <ArrowUpIcon size={15} color={palette.success[500]} />
                                <Text size="textSm" palette="neutral" shade="600">
                                    {t('accesses:more_accesses', {
                                        accesses: Math.abs(data?.treatments.difference)
                                    })}
                                </Text>
                            </Flexbox>
                        ) : (
                            <Flexbox align="center" gap={1}>
                                <StyledDot />
                                <Text size="textSm" palette="neutral" shade="600">
                                    {t('accesses:equal_accesses', {accesses: 0})}
                                </Text>
                            </Flexbox>
                        )}
                    </>
                )}
            </Flexbox>
        </AccessDashboardCard>
    )
}
